export type WorkflowPayloadMode = "input" | "messages";

export type ParsedWorkflowBody =
  | { ok: true; input?: Record<string, unknown>; messages?: unknown[] }
  | { ok: false; error: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

export function parseWorkflowRequestBody(
  mode: WorkflowPayloadMode,
  inputJson: string,
  messagesJson: string
): ParsedWorkflowBody {
  if (mode === "input") {
    const trimmed = inputJson.trim();
    const src = trimmed.length ? trimmed : "{}";
    try {
      const parsed = JSON.parse(src) as unknown;
      if (!isPlainObject(parsed)) {
        return { ok: false, error: "Input must be a JSON object." };
      }
      return { ok: true, input: parsed };
    } catch {
      return { ok: false, error: "Invalid JSON in request input." };
    }
  }

  const trimmed = messagesJson.trim();
  if (!trimmed) {
    return { ok: false, error: "Please enter a message." };
  }

  // Try parsing as JSON array first (power-user path)
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed) && parsed.length > 0) {
        for (let i = 0; i < parsed.length; i++) {
          const m = parsed[i];
          if (!isPlainObject(m) || typeof m.role !== "string") {
            return {
              ok: false,
              error: `Message at index ${i} must be an object with a string "role".`,
            };
          }
        }
        return { ok: true, messages: parsed };
      }
    } catch {
      // Fall through to freeform handling
    }
  }

  // Freeform text — wrap into a single user message
  const sanitized = trimmed.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
  return { ok: true, messages: [{ role: "user", content: sanitized }] };
}

export const DEFAULT_WORKFLOW_INPUT_JSON = "{}";
export const DEFAULT_WORKFLOW_MESSAGES_JSON = "";
